import { Component, EventEmitter, OnInit, Input, Output, ViewChild, AfterViewInit, ElementRef, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { CustomerService } from '../../../services/customer.service';
import { UsersService } from '../../../services/users.service';
import { OmsService } from '../../../services/oms.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-customer-lookup',
	templateUrl: './customer-lookup.component.html',
	styleUrls: ['./customer-lookup.component.scss']
})
export class CustomerLookupComponent implements OnInit, AfterViewInit, OnDestroy {

	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild('customerinput') customerinput: ElementRef;
	@ViewChild('resultsWrapper') resultsWrapper: ElementRef;
	@Output() customer_selected = new EventEmitter < any > ();
	@Input() creating = false;
	@Input() lookupOnly: any = false;

	title = 'customers';
	outlined = 'outline';
	customers: any[] = [];
	all_customers: any[] = [];
	headercolumns: string[] = ['actions', 'name', 'billingaddress','contact','address3'];
	customersearchservice: any;
	timeout: any = null;
	searching = false;
	customersearching = false;
	customersearchsubscription: any;
	noresults = false;
	ready = true;
	sending = false;
	color: any = 'blue';
	customertypes: any = false;
	customertype = new UntypedFormControl('');
	customersearch = new UntypedFormControl('');
	include_disabled = new UntypedFormControl(false);
	search_by_order = new UntypedFormControl(false);
	phoneonly = new UntypedFormControl(false);
	config: any = false;
	user: any = false;
	dataSource: MatTableDataSource < any > = new MatTableDataSource();
	isPosUser: any = false;
	highlightedRowIndex = -1;

	// Memory leak prevention
	private destroy$ = new Subject<void>();

	constructor(
		private usersService: UsersService,
		private fb: UntypedFormBuilder,
		private globalSearchService: GlobalSearchService,
		public customerService: CustomerService,
		public router: Router,
		public omsService: OmsService
	) {
		this.omsService.setPrintedOrder();
		this.globalSearchService.configsubscription.pipe(takeUntil(this.destroy$)).subscribe(r => {
			this.config = r;
		});

		this.globalSearchService.userData.pipe(takeUntil(this.destroy$)).subscribe(r => {
			this.user = r;
		});
	}

	ngOnInit(): void {
		this.ready = false;
		this.noresults = true;
		this.color = this.globalSearchService.getColor();
		this.globalSearchService.mesagesallowed.next(true);
		if (this.creating) {
			this.outlined = 'outline';
		}
		this.globalSearchService.customertypes.pipe(takeUntil(this.destroy$)).subscribe(r => {
			this.customertypes = r;
		});
		this.usersService.isPosUser().pipe(takeUntil(this.destroy$)).subscribe(isPOS => {
			if (isPOS) {
				this.isPosUser = true;
			}
			if (this.customerinput?.nativeElement) {
				this.customerinput.nativeElement.focus();
			}
		});


	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;
		this.dataSource.paginator = this.paginator;
		if (this.paginator?.page) {
			this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe(() => {
				this.highlightedRowIndex = -1;
			});
		}
	}

	filterCustomers() {
	  this.ready = false;

	  if (this.customersearchsubscription) {
			this.customersearchsubscription.unsubscribe();
	  }

	  if (this.customersearch.value === '') {
			this.clearForm();
			return false;
	  }

	  const search = {
			customertype: this.customertype.value,
			keywords: this.customersearch.value,
			include_disabled: this.include_disabled.value,
			search_by_order: this.search_by_order.value,
			phoneonly: this.phoneonly.value
	  };

	  this.sending = true;
	  this.customersearching = true;

	  this.customersearchsubscription = this.customerService.getCustomerSearch(search).pipe(takeUntil(this.destroy$)).subscribe((results: any[]) => {
			this.sending = false;

			this.all_customers = results;

			// Clone results before filtering
			let filtered = [...results];

			if (!this.include_disabled.value) {
		  filtered = filtered.filter(c => c.disabletrans !== true);
			}

			// Prepare memoized billing display
			filtered.forEach(customer => {
		  const stripSlashes = (str: string): string => str ? str.replace(/^\/+|\/+$/g, '') : '';
		  const address = stripSlashes(customer.billingaddress);
		  const email = stripSlashes(customer.email);
		  customer._renderedBilling = address;
		  if (email) {
					customer._renderedBilling += `<br /><span class="mat-caption">EML: <b>${email}</b></span>`;
		  }
			});

			this.customers = filtered;
			this.dataSource.data = filtered;

			this.customersearching = false;
			this.ready = true;
			this.noresults = filtered.length === 0;
			this.highlightedRowIndex = -1;

			if (filtered.length === 1) {
		  this.customer_selected.emit(filtered[0]);
		  this.clearForm();
			} else if (this.user?.user?.issalesman) {
		  this.globalSearchService.blurInputElement(this.customerinput);
			}
	  });
	}

	getCurrentPageRows(): any[] {
		const data = this.dataSource.filteredData || [];
		if (!this.paginator) {
			return data;
		}
		const start = this.paginator.pageIndex * this.paginator.pageSize;
		return data.slice(start, start + this.paginator.pageSize);
	}

	onSearchKeydown(event: KeyboardEvent): void {
		const rows = this.getCurrentPageRows();
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			if (!rows.length) {
				return;
			}
			event.preventDefault();
			if (event.key === 'ArrowDown') {
				this.highlightedRowIndex = Math.min(this.highlightedRowIndex + 1, rows.length - 1);
			} else {
				this.highlightedRowIndex = Math.max(this.highlightedRowIndex - 1, 0);
			}
			this.scrollHighlightedIntoView();
			return;
		}
		if (event.key === 'Enter') {
			event.preventDefault();
			if (rows.length && this.highlightedRowIndex >= 0 && rows[this.highlightedRowIndex]) {
				this.selectCustomer(rows[this.highlightedRowIndex]);
			} else if (!rows.length) {
				this.filterCustomers();
			}
		}
	}

	scrollHighlightedIntoView(): void {
		setTimeout(() => {
			const el = this.resultsWrapper?.nativeElement?.querySelectorAll?.('mat-row')?.[this.highlightedRowIndex];
			if (el) {
				el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
			}
		}, 0);
	}


	clearForm() {
		this.noresults = true;
		this.customersearching = false;
		this.customers = [];
		this.all_customers = [];
		this.customersearch.setValue('');
		this.dataSource.data = [];
		this.ready = false;
		this.highlightedRowIndex = -1;
	}

	filterDisabled() {
		if (this.include_disabled.value) {
			this.customers = this.all_customers;
		} else {
			this.customers = this.all_customers.filter((customer) => {
				return customer.disabletrans === this.include_disabled.value;
			});
		}
		this.dataSource.data = this.customers;
	}

	selectCustomer(row: any) {
		this.clearForm();
		this.omsService.setPrintedOrder();
		this.customer_selected.emit(row);
		this.ready = false;
	}

	getCustomerInfo(customer: any): string {
		const stripSlashes = (str: string): string => str ? str.replace(/^\/+|\/+$/g, '') : '';

		let customerInfo = `
	  <strong>${stripSlashes(customer.debtorno)}.${stripSlashes(customer.branchcode)}</strong>
	`;

		if (customer.name) {
			customerInfo += `<br />${stripSlashes(customer.name)}`;
		}

		if (customer.phoneno) {
			customerInfo += `<br />${stripSlashes(customer.phoneno)}`;
		}

		if (this.config.showsalesman && customer.salesmanname) {
			customerInfo += `<br /><span class="mat-caption">SLSM: <b>${stripSlashes(customer.salesmanname)}</b></span>`;
		}


		return customerInfo;
	}

	ngOnDestroy(): void {
		// Complete destroy$ subject to unsubscribe all takeUntil subscriptions
		this.destroy$.next();
		this.destroy$.complete();

		// Unsubscribe search subscription if it exists
		if (this.customersearchsubscription && typeof this.customersearchsubscription.unsubscribe === 'function') {
			this.customersearchsubscription.unsubscribe();
		}

		// Clear timeout if it exists
		if (this.timeout) {
			clearTimeout(this.timeout);
			this.timeout = null;
		}
	}
}
